import React, { useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useAsyncFn } from 'react-use';
import { closestCenter, DndContext, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import cn from 'classnames';

import {
  ibeKpiSelectionsCreate,
  ibeKpiSelectionsDelete,
  ibeKpiSelectionsMove,
} from 'actions/kpi/beKpiSelection.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import TruncatedText from 'components/generic/TruncatedText.component';
import KpiAddDropdown from 'components/hr/KpiAddDropdown.component';
import TOOLTIP_METRIC_TEXT_PAST from 'components/kpi/TooltipMetricTextPast.component';
import FLAG from 'config/flag.singleton';
import DELAY from 'constants/delay.const';
import S from 'constants/string.const';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import call from 'utils/functions/call.util';
import delayed from 'utils/functions/delayed.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isFn from 'utils/predicates/function/isFn.util';
import isPromise from 'utils/predicates/isPromise.util';

import CN from './ScoreCardEdit.module.scss';

const L = logger('ScoreCardEdit');

const USE_SPAMMY_LOG = fromAnyToBool(FLAG.loggerSpam);

const CARD_HEIGHT = 520;
// delay in milliseconds - the user must hold the element for at least that much before starting to move it
const SENSOR_DELAY_MS = 100;

// tolerance in pixels - touch must move at least that far in any direction before the move is activated
const SENSOR_TOLERANCE_PX = 5;

const calculateTooltipText = (metricKey) => {
  const cleanMetricKey = metricKey.split('|')[0]; // NOTE: we will only use the part before '|'-> revenue_per_category|77609
  return TOOLTIP_METRIC_TEXT_PAST[cleanMetricKey] || 'Default text';
};

const TooltipContent = ({ metricKey }) => (
  <div data-bem="TooltipContent" className={CN.textTooltip}>
    {calculateTooltipText(metricKey)}
  </div>
);

const finalize = (fn, promise) => {
  const isF = isFn(fn);
  if (!isF) {
    L.warn('finalize()', 'Invalid function:', fn, 'nothing to execute');
    return promise;
  }

  const isP = isPromise(promise);
  if (!isP) {
    L.info('finalize()', 'Invalid promise:', promise, 'executing right away');
    fn();
    return promise;
  }

  promise.finally(fn);
  return promise;
};

const determineDndStyles = ({ transform, isDragging, transition }) => {
  const transformY = transform?.y || 0;
  const rotationDegrees = isDragging ? '-2deg' : '0deg';
  const transitionStyle = isDragging ? 'none' : transition || 'transform 0.25s ease';

  return {
    styleItem: {
      transform: `translate3d(0, ${transformY}px, 0) rotate(${rotationDegrees})`,
      border: isDragging ? '2px solid #821bb8' : '',
      transition: transitionStyle,
      touchAction: 'none', // NOTE: ! very important for touch devices
    },
    styleDeleteContainer: {
      transform: `translate3d(0, ${transformY}px, 0) rotate(${rotationDegrees})`,
      transition: transitionStyle,
    },
  };
};

const SortableItem = ({ sortKey, value, onDelete }) => {
  const metricKey = value?.metricKey;
  const id = value?.id;

  const { val: renderModal, yay: showModal, nay: hideModal } = useTip(false);
  // noinspection JSCheckFunctionSignatures
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortKey });
  const { styleItem, styleDeleteContainer } = determineDndStyles({ transform, isDragging, transition });

  const handleDelete = useCallback(
    () => void call('SortableItem.onDelete', onDelete, { ...asObject(value) }),
    [onDelete, value],
  );

  return (
    <div data-bem="SortableItem" data-id={String(id)} data-key={String(metricKey)} className={CN.sortableItemContainer}>
      <div {...attributes} {...listeners} ref={setNodeRef} style={styleItem} className={CN.sortableItem}>
        <Icon variant="drag" width="lg" />
      </div>

      <div data-bem="SortableItem__delete" className={CN.buttonDeleteContainer} style={styleDeleteContainer}>
        <div data-bem="SortableItem__label" className={CN.text}>
          <TruncatedText max={64} suffix="..." value={value?.label} />

          <Button variant="bin" onClick={showModal}>
            <Icon variant="info" width="md" />
          </Button>
        </div>

        <Button variant="bin" onClick={handleDelete}>
          <Icon variant="delete" width="lg" />
        </Button>
      </div>

      <SalonModal tag="ScoreCardEdit__modal--metric" isOpen={renderModal} variant="medium">
        <SalonModal.Header>
          {value?.label}
          <Button variant="empty" onClick={hideModal}>
            <Icon variant="close" />
          </Button>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContainer}>
            <TooltipContent metricKey={metricKey} />
          </div>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

const ScoreCardEdit = ({
  scope: scopeFromProps,
  value,
  stylistName,
  isEmptyPersonal,
  onChange,
  ibeKpiSelectionsDelete,
  ibeKpiSelectionsCreate,
  ibeKpiSelectionsMove,
}) => {
  const { isBusiness, stylistId, selected, available } = value ?? {};

  const scope = isBusiness ? 'business' : scopeFromProps;
  const items = asArray(selected);
  const displayName = isBusiness ? 'Business' : fromAnyToTrimmed(stylistName) || 'Unknown Stylist Name';
  const ids = asArray(items.map((item) => item?.id));

  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();
  const cardRef = useRef(null);

  const [message, setMessage] = useStateWhileMounted('');
  const { val: isSavedRendered, yay: yaySaved, nay: naySaved } = useTip(false);
  const { val: isErrorRendered, yay: yayError, nay: nayError } = useTip(false);
  const { val: dropdownHeight, yay: markCardsAsShort, nay: markCardsAsTall } = useTip(false);

  const showMessage = useCallback(
    (text) => {
      nayError();
      naySaved();
      setMessage(fromAnyToString(text));
    },
    [nayError, naySaved, setMessage],
  );

  const showSaved = useCallback(() => {
    showMessage('');
    nayError();
    yaySaved();
    delayed(DELAY.autohide, naySaved);
  }, [nayError, naySaved, showMessage, yaySaved]);

  const showError = useCallback(() => {
    showMessage('');
    naySaved();
    yayError();
    delayed(DELAY.autohide, naySaved);
  }, [naySaved, showMessage, yayError]);

  const { val: isAddDropdownRendered, tip: toggleAddDropdown, nay: closeAddDropdown } = useTip(false);
  const kpisRef = useRef(null);
  useOnClickOutside(kpisRef, closeAddDropdown);

  // NOTE: There is also the KeyboardSensor for dragging via the keyboard.
  const sensorOptions = { activationConstraint: { delay: SENSOR_DELAY_MS, tolerance: SENSOR_TOLERANCE_PX } };
  const sensors = useSensors(useSensor(PointerSensor, sensorOptions), useSensor(TouchSensor, sensorOptions));

  const handleDragStart = useCallback((ev) => void L.debug('handleDragStart()', ev), []);
  const handleDragMove = alwaysNoop; // less spammy than: useCallback((ev) => void L.debug('handleDragMove()', ev), []);

  const [resultDragEnd, handleDragEnd] = useAsyncFn(
    async (ev) => {
      L.debug('handleDragEnd()', scope, ev, items);

      const dragId = ev?.active?.id;
      const dropId = ev?.over?.id;
      if (dragId === dropId) return;

      showMessage('Reordering');

      const toPosition = items.findIndex((item) => item?.id === dropId);
      const result = await ibeKpiSelectionsMove({ scope, id: dragId, data: { toPosition } });

      if (result.is.ok) {
        finalize(showSaved, call('ScoreCardEdit.onChange', onChange, { type: 'move', scope, id: dragId }));
      } else {
        showError();
      }

      return result;
    },
    [scope, items, showMessage, ibeKpiSelectionsMove, showSaved, onChange, showError],
  );

  const [resultDelete, handleDelete] = useAsyncFn(
    async (ev) => {
      L.debug('handleDelete()', scope, ev);

      showMessage(`Removing ${fromAnyToTrimmed(ev?.label)}`);

      const id = ev?.id;
      const result = await ibeKpiSelectionsDelete({ scope, id });

      if (result.is.ok) {
        finalize(showSaved, call('ScoreCardEdit.onChange', onChange, { type: 'delete', scope, id }));
      } else {
        showError();
      }

      return result;
    },
    [scope, showMessage, ibeKpiSelectionsDelete, showSaved, onChange, showError],
  );

  const [resultAdd, handleAdd] = useAsyncFn(
    async (ev) => {
      const data = { ...asObject(ev?.raw), salonId };
      L.debug('handleAdd()', scope, data, ev);

      closeAddDropdown();

      showMessage(`Adding ${fromAnyToTrimmed(data?.label)}`);
      const result = await ibeKpiSelectionsCreate({ scope, data });

      if (result.is.ok) {
        finalize(showSaved, call('ScoreCardEdit.onChange', onChange, { type: 'add', scope }));
      } else {
        showError();
      }

      return result;
    },
    [salonId, scope, closeAddDropdown, showMessage, ibeKpiSelectionsCreate, showSaved, onChange, showError],
  );

  const isMessageRendered = fromAnyToBool(message);
  const isEmpty = !isFullArray(items);

  useEffect(() => {
    if (cardRef.current) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const cardHeight = entry.contentRect.height; // detects the change and retrieves the new height

          if (cardHeight > CARD_HEIGHT) {
            markCardsAsTall();
            return;
          }
        }
        markCardsAsShort();
      });

      observer.observe(cardRef.current);

      return () => observer.disconnect();
    }
  }, [markCardsAsTall, markCardsAsShort]);

  if (USE_SPAMMY_LOG) L.debug('()', resultDelete, resultAdd, resultDragEnd);

  return (
    <div
      ref={cardRef}
      data-bem="ScoreCardEdit"
      data-is-business={String(isBusiness)}
      data-is-empty-personal={String(isEmptyPersonal)}
      data-scope={String(scope)}
      data-stylist-id={String(stylistId)}
      data-stylist-name={String(stylistName)}
      className={cn({
        [CN.component]: true,
        [CN.empty]: isEmpty,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="ScoreCardEdit__sticky" className={CN.stickyContainer}>
        <div data-bem="ScoreCardEdit__header" className={CN.header}>
          <div className={CN.stylistName}>
            <TruncatedText max={30} suffix="..." value={displayName} />
          </div>
          <div className={CN.flex}>
            {isMessageRendered && (
              <>
                <InlineSpinner variant="small" />
                <span className={CN.message}>{message}</span>
              </>
            )}
            {isSavedRendered && (
              <div className={CN.flex}>
                <Icon variant="confirm" width="lg" />
                <span className={CN.message}>Saved</span>
              </div>
            )}
            {isErrorRendered && (
              <div className={CN.flex}>
                <Icon variant="error" width="lg" />
                <span className={CN.message}>Error</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div data-bem="ScoreCardEdit__body" className={CN.container}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {isEmpty ? (
              <div className={CN.noData}>
                <div className={CN.title}>
                  Add the KPIs you want to track by clicking the "Add KPIs" button {S.eyes}
                </div>
              </div>
            ) : (
              items.map((item) => (
                <SortableItem key={item?.id} sortKey={item?.id} value={item} onDelete={handleDelete} />
              ))
            )}
          </SortableContext>
        </DndContext>
      </div>

      <div data-bem="ScoreCardEdit__footer" className={CN.cardFooter}>
        <div data-bem="ScoreCardEdit__add" className={CN.alignStart} ref={kpisRef}>
          <Button variant="tertiary" tag="ScoreCardEdit_add-kpi" onClick={toggleAddDropdown}>
            <Icon variant="plus" width="lg" />
            <div className={CN.spaceRight}>ADD KPI</div>
          </Button>

          {isAddDropdownRendered && (
            <KpiAddDropdown
              height={dropdownHeight}
              options={available}
              onAddKpi={handleAdd}
              position={isEmpty ? 'bottom' : 'top'}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const ScoreCardEditHoc = connect(
  null,
  mapActionsToDispatchToProps({ ibeKpiSelectionsDelete, ibeKpiSelectionsCreate, ibeKpiSelectionsMove }),
)(ScoreCardEdit);

export default ScoreCardEditHoc;
